import {
  ActionContext as VuexActionContext,
  Store
} from 'vuex'
import { Namespace } from '@/store/Namespaces'
import { submitLandlordOrderForm } from '@/services/landlord.service'
import { OrderFormFields } from '@/types/landlord-order.types'

export const namespace = (fragment: string) => `${Namespace.LandlordOrder}/${Namespace.LandlordOrderSummary}/${fragment}`

export enum Property {
  OrderSubmitted = 'orderSubmitted',
  OrderBeingSubmitted = 'orderBeingSubmitted',
  OrderError = 'orderError',
  CaseId = 'caseId',
}

enum Setter {
  SetOrderSubmitted = 'setOrderSubmitted',
  SetFormBeingSubmitted = 'setFormBeingSubmitted',
  SetOrderError = 'setOrderError',
  SetCaseId = 'setCaseId',
}

enum Action {
  SetOrderSubmitted = 'setOrderSubmitted',
  SetCaseId = 'setCaseId',
  ResetSummary = 'resetSummary',
  SubmitOrderForm = 'submitOrderForm'
}

interface State {
  [Property.OrderBeingSubmitted]: boolean;
  [Property.OrderSubmitted]: boolean;
  [Property.OrderError]: boolean;
  [Property.CaseId]: string;
}

export type ActionContext = VuexActionContext<State, {}>

export const getOrderSubmitted = (store: Store<{}>): boolean => store.getters[namespace(Property.OrderSubmitted)]
export const getOrderBeingSubmitted = (store: Store<{}>): boolean => store.getters[namespace(Property.OrderBeingSubmitted)]
export const getOrderError = (store: Store<{}>): boolean => store.getters[namespace(Property.OrderError)]
export const getCaseId = (store: Store<{}>): string => store.getters[namespace(Property.CaseId)]

export const submitOrderForm = (store: Store<{}>, fields: OrderFormFields): Promise<void> => store.dispatch(namespace(Action.SubmitOrderForm), fields)

export const resetSummary = (store: Store<{}>): Promise<void> => store.dispatch(namespace(Action.ResetSummary))

const landLordOrderSummaryModule = {
  namespaced: true,
  state: {
    [Property.OrderSubmitted]: false,
    [Property.OrderBeingSubmitted]: false,
    [Property.OrderError]: false,
    [Property.CaseId]: null
  },
  getters: {
    [Property.OrderSubmitted]: (state: State) => state[Property.OrderSubmitted],
    [Property.OrderBeingSubmitted]: (state: State) => state[Property.OrderBeingSubmitted],
    [Property.OrderError]: (state: State) => state[Property.OrderError],
    [Property.CaseId]: (state: State) => state[Property.CaseId]
  },
  mutations: {
    [Setter.SetOrderSubmitted]: (state: State, status: boolean) => {
      state[Property.OrderSubmitted] = status
    },
    [Setter.SetFormBeingSubmitted]: (state: State, status: boolean) => {
      state[Property.OrderBeingSubmitted] = status
    },
    [Setter.SetOrderError]: (state: State, error: boolean) => {
      state[Property.OrderError] = error
    },
    [Setter.SetCaseId]: (state: State, value: string) => {
      state[Property.CaseId] = value
    }
  },
  actions: {
    [Action.SetOrderSubmitted]: ({ commit }: ActionContext, status: boolean): void => {
      commit(Setter.SetOrderSubmitted, status)
    },
    [Action.SetCaseId]: ({ commit }: ActionContext, value: string): void => {
      commit(Setter.SetCaseId, value)
    },
    [Action.ResetSummary]: ({ commit }: ActionContext): void => {
      commit(Setter.SetOrderSubmitted, false)
      commit(Setter.SetFormBeingSubmitted, false)
      commit(Setter.SetOrderError, false)
      commit(Setter.SetCaseId, null)
    },
    [Action.SubmitOrderForm]: async ({ commit }: ActionContext, fields: OrderFormFields): Promise<void> => {
      try {
        commit(Setter.SetOrderError, false)
        commit(Setter.SetFormBeingSubmitted, true)

        const data = await submitLandlordOrderForm(fields)

        commit(Setter.SetOrderSubmitted, true)
        commit(Setter.SetCaseId, data)
      } catch (_) {
        commit(Setter.SetOrderSubmitted, false)
        commit(Setter.SetOrderError, true)
      } finally {
        commit(Setter.SetFormBeingSubmitted, false)
      }
    }
  }
}

export default landLordOrderSummaryModule
